import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Pencil, ExternalLink as ExternalLinkIcon } from 'lucide-react';
import { api, mediaUrl } from '@/lib/api';
import { ADMIN } from '@/constants/testIds';
import ContentTypePicker from '@/components/admin/ContentTypePicker';
import {
  Drawer,
  AudioForm,
  VideoForm,
  ArticleForm,
  ProductForm,
  GalleryForm,
  ExternalLinkForm,
  ADMIN_DEFAULTS,
} from '@/pages/AdminDashboardPage';

/**
 * MixedChannelAdmin
 * -----------------
 * Admin management view for user-created channels that don't have a dedicated
 * legacy CrudAdmin tab. Aggregates ALL content types filtered by channel_key
 * === channelKey. Uses the ContentTypePicker for the Add Content workflow.
 *
 * The editor drawer for each type is delegated back to the existing per-type
 * endpoints (audio/articles/videos/products/gallery) so no new backend routes
 * are required — this is purely an aggregation view.
 *
 * Props:
 *   channelKey   string (required)
 *   channelLabel string (recommended)
 */

// Registry of content sources this view is aware of. Each entry knows its
// endpoint and how to render a compact row + edit link.
const SOURCES = [
  { key: 'audio',    endpoint: 'audio',    typeLabel: 'AUDIO',     legacyTypes: ['audio'] },
  { key: 'video',    endpoint: 'videos',   typeLabel: 'VIDEO',     legacyTypes: ['video'] },
  { key: 'article',  endpoint: 'articles', typeLabel: 'ARTICLE',   legacyTypes: ['article', 'standard', 'external_link'] },
  { key: 'product',  endpoint: 'products', typeLabel: 'PRODUCT',   legacyTypes: ['product', 'digital_download'] },
  { key: 'gallery',  endpoint: 'gallery',  typeLabel: 'GALLERY',   legacyTypes: ['gallery'] },
];

export default function MixedChannelAdmin({ channelKey, channelLabel }) {
  const [items, setItems] = useState([]); // { source, ...doc }
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [notice, setNotice] = useState(null);

  // ---- Edit drawer state ----
  const [editing, setEditing] = useState(null);      // the row being edited (with __source, __endpoint)
  const [editForm, setEditForm] = useState({});      // working copy of the doc
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!channelKey) return;
    setLoading(true);
    try {
      // Load every content source in parallel and combine, filtering by channel_key.
      const results = await Promise.all(SOURCES.map(async (s) => {
        try {
          const { data } = await api.get(`/admin/${s.endpoint}`);
          return (data || [])
            .filter((it) => it && it.channel_key === channelKey)
            .map((it) => ({ ...it, __source: s.key, __endpoint: s.endpoint }));
        } catch (_) { return []; }
      }));
      const flat = [].concat(...results);
      flat.sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
      setItems(flat);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [channelKey]);

  const onPickType = async (typeKey) => {
    setPickerOpen(false);
    // Map picker type → endpoint + baseline body (channel_key auto-populated).
    const map = {
      audio:            { endpoint: 'audio',    body: { title: 'New audio', kind: 'music', source_type: 'upload', file_url: '', embed_url: '', duration: '', cover_url: '', description: '', tags: [], published: true, featured: false, publish_status: 'draft' } },
      video:            { endpoint: 'videos',   body: { title: 'New video', embed_url: 'https://', description: '', poster_url: '', tags: [], published: true, featured: false, publish_status: 'draft' } },
      article:          { endpoint: 'articles', body: { title: 'New article', slug: `article-${Date.now()}`, body: '', subtitle: '', excerpt: '', cover_url: '', tags: [], read_time: '', published: true, featured: false, publish_status: 'draft', content_type: 'article' } },
      standard:         { endpoint: 'articles', body: { title: 'New standard content', slug: `standard-${Date.now()}`, body: '', subtitle: '', excerpt: '', cover_url: '', tags: [], read_time: '', published: true, featured: false, publish_status: 'draft', content_type: 'standard' } },
      external_link:    { endpoint: 'articles', body: { title: 'New external link', slug: `link-${Date.now()}`, body: '', subtitle: '', excerpt: '', cover_url: '', tags: [], read_time: '', published: true, featured: false, publish_status: 'draft', content_type: 'external_link', external_url: 'https://' } },
      product:          { endpoint: 'products', body: { title: 'New product', description: '', price: 9.99, currency: 'usd', published: false, featured: false, publish_status: 'draft', fulfillment_provider: 'manual', is_subscription: false, content_type: 'product' } },
      digital_download: { endpoint: 'products', body: { title: 'New digital download', description: '', price: 4.99, currency: 'usd', published: false, featured: false, publish_status: 'draft', fulfillment_provider: 'digital', is_subscription: false, content_type: 'digital_download' } },
      gallery:          { endpoint: 'gallery',  body: { title: 'New gallery item', caption: '', image_url: 'https://placehold.co/1200x800/png', tags: [], published: true, publish_status: 'draft' } },
    };
    const cfg = map[typeKey];
    if (!cfg) { alert('Unsupported content type'); return; }
    try {
      await api.post(`/admin/${cfg.endpoint}`, { ...cfg.body, channel_key: channelKey });
      setNotice(`Created a ${typeKey.replace('_', ' ')} draft in this channel — click Edit on the row below to open the full editor.`);
      await load();
      setTimeout(() => setNotice(null), 6000);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Create failed');
    }
  };

  const del = async (item) => {
    if (!window.confirm(`Remove "${item.title || item.id}" from ${channelLabel || channelKey}?`)) return;
    try {
      await api.delete(`/admin/${item.__endpoint}/${item.id}`);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Delete failed');
    }
  };

  // Given a row that already knows its __source, pick the right form renderer
  // + defaults. Articles branch on content_type so external links get the
  // dedicated ExternalLinkForm.
  const resolveEditor = (item) => {
    switch (item.__source) {
      case 'audio': {
        const kind = item.kind || 'music';
        const base = kind === 'podcast' ? ADMIN_DEFAULTS.audio_podcast : ADMIN_DEFAULTS.audio_music;
        return { renderForm: AudioForm, defaults: base, labelOne: kind === 'podcast' ? 'episode' : 'track' };
      }
      case 'video':
        return { renderForm: VideoForm, defaults: ADMIN_DEFAULTS.videos, labelOne: 'video' };
      case 'article': {
        if (item.content_type === 'external_link') {
          return {
            renderForm: ExternalLinkForm,
            defaults: {
              ...ADMIN_DEFAULTS.articles,
              content_type: 'external_link',
              external_url: '',
            },
            labelOne: 'external link',
          };
        }
        return { renderForm: ArticleForm, defaults: ADMIN_DEFAULTS.articles, labelOne: item.content_type === 'standard' ? 'standard content' : 'article' };
      }
      case 'product':
        return { renderForm: ProductForm, defaults: ADMIN_DEFAULTS.products, labelOne: item.content_type === 'digital_download' ? 'digital download' : 'product' };
      case 'gallery':
        return { renderForm: GalleryForm, defaults: ADMIN_DEFAULTS.gallery, labelOne: 'gallery item' };
      default:
        return null;
    }
  };

  const openEdit = (item) => {
    const editor = resolveEditor(item);
    if (!editor) {
      alert('This content type is not editable from this view.');
      return;
    }
    setEditing(item);
    // Merge defaults so any newly-added fields have safe fallbacks in the form.
    setEditForm({ ...editor.defaults, ...item });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      // Strip UI-only helper keys (prefixed with __ or _) before PUT.
      const clean = Object.fromEntries(
        Object.entries(editForm).filter(([k]) => !k.startsWith('_'))
      );
      await api.put(`/admin/${editing.__endpoint}/${editing.id}`, clean);
      setEditOpen(false);
      setEditing(null);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const activeEditor = editing ? resolveEditor(editing) : null;

  const grouped = useMemo(() => {
    const g = {};
    for (const it of items) {
      const bucket = it.__source;
      (g[bucket] = g[bucket] || []).push(it);
    }
    return g;
  }, [items]);

  return (
    <div data-testid="mixed-channel-admin">
      <div className="pg-admin-bar flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <p className="pg-label">CHANNEL · {(channelLabel || channelKey || '').toUpperCase()}</p>
          <p className="text-xs text-[var(--pg-ink-muted)] mt-1 max-w-2xl">
            All content assigned to this channel (any type). Content types determine behavior;
            this channel simply organizes them. Full-featured editing continues to live in each
            content type&apos;s dedicated tab (Music, Watch, On My Mind, Vault, etc.).
          </p>
        </div>
        <button
          onClick={() => setPickerOpen(true)}
          data-testid={ADMIN.createButton}
          className="pg-btn-primary rounded-[var(--pg-radius-md)] px-4 py-2 text-xs font-mono tracking-widest inline-flex items-center gap-2"
        >
          <Plus size={14} /> ADD CONTENT
        </button>
      </div>

      {notice && (
        <div className="pg-card p-3 mb-4 text-xs text-[var(--pg-emerald-2)] font-mono" data-testid="mixed-channel-notice">
          {notice}
        </div>
      )}

      {loading ? (
        <div className="pg-card p-10 text-center text-[var(--pg-ink-muted)]">Loading content…</div>
      ) : items.length === 0 ? (
        <div className="pg-card p-10 text-center text-[var(--pg-ink-muted)]" data-testid="mixed-channel-empty">
          No content assigned to this channel yet. Use the <span className="font-mono text-[var(--pg-ink)]">ADD CONTENT</span> button to place items here.
        </div>
      ) : (
        <div className="space-y-6">
          {SOURCES.map((s) => {
            const list = grouped[s.key] || [];
            if (list.length === 0) return null;
            return (
              <div key={s.key} data-testid={`mixed-channel-source-${s.key}`}>
                <p className="pg-label text-[10px] mb-2">{s.typeLabel} · {list.length}</p>
                <div className="pg-card overflow-hidden">
                  <ul className="divide-y divide-[var(--pg-border-2)]">
                    {list.map((it) => (
                      <li key={`${it.__source}:${it.id}`} className="p-3 flex items-center gap-3">
                        <div className="h-10 w-10 rounded-md overflow-hidden bg-[var(--pg-navy-800)] shrink-0 border border-[var(--pg-border-2)]">
                          {(it.cover_url || it.image_url || it.poster_url) && (
                            <img src={mediaUrl(it.cover_url || it.image_url || it.poster_url)} alt="" className="w-full h-full object-cover" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-serif text-sm text-[var(--pg-ink)] truncate">{it.title}</p>
                          <p className="text-[10px] font-mono text-[var(--pg-ink-faint)] tracking-widest uppercase truncate">
                            {it.content_type || it.kind || s.typeLabel.toLowerCase()}
                            {it.publish_status ? ` · ${it.publish_status}` : ''}
                            {it.external_url ? ' · external' : ''}
                          </p>
                        </div>
                        {it.external_url && <ExternalLinkIcon size={13} className="text-[var(--pg-ink-faint)]" />}
                        <button
                          onClick={() => openEdit(it)}
                          data-testid="mixed-channel-edit"
                          className="pg-btn-ghost rounded-[var(--pg-radius-sm)] px-3 py-1.5 text-xs inline-flex items-center gap-1"
                          aria-label={`Edit ${it.title}`}
                        >
                          <Pencil size={13} /> <span className="hidden sm:inline">Edit</span>
                        </button>
                        <button
                          onClick={() => del(it)}
                          data-testid={ADMIN.deleteButton}
                          className="pg-btn-ghost rounded-[var(--pg-radius-sm)] px-3 py-1.5 text-xs text-[var(--pg-neon-pink)]"
                          aria-label={`Remove ${it.title}`}
                        >
                          <Trash2 size={13} />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ContentTypePicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={onPickType}
        channelKey={channelKey}
        channelLabel={channelLabel}
      />

      {/* Universal edit drawer — reuses the same form used by the item's
          native admin tab so all fields are editable in one place. */}
      <Drawer
        open={editOpen}
        onClose={() => { setEditOpen(false); setEditing(null); }}
        title={editing ? `Edit ${activeEditor?.labelOne || 'content'}` : 'Edit'}
        onSave={saveEdit}
        saving={saving}
      >
        {editing && activeEditor && activeEditor.renderForm(editForm, setEditForm)}
      </Drawer>
    </div>
  );
}
